import { useEffect, useState } from "react";
import { Box, Button } from "@mui/material";
import { AdvancedMarker } from "@vis.gl/react-google-maps";
import LargeDialog from "components/LargeDialog";
import MapSearchField from "./MapSearchField";
import Check from "@mui/icons-material/Check";
import Clear from "@mui/icons-material/Clear";
import { Address, getAddressDetails } from "./util/getAddressDetails";
import { formatAddress } from "./util/formatAddress";
import MapToolbar from "./MapToolbar";
import Map from "./Map";
import Dot from "./Dot";

type AddressPickerDialogProps = {
  initialAddress?: Address;
  onSelectAddress: (address: Address) => void;
  onClose: () => void;
};

const AddressPickerDialog: React.FC<AddressPickerDialogProps> = ({
  initialAddress,
  onSelectAddress,
  onClose,
}) => {
  // const SELECTED_MARKER = undefined; // Use default marker icon for selected place

  // const UNSELECTED_MARKER: google.maps.Symbol = {
  //   path: google.maps.SymbolPath.CIRCLE,
  //   fillColor: "#e1412f",
  //   fillOpacity: 1,
  //   strokeWeight: 0.2,
  //   scale: 6,
  // } as const;

  const [query, setQuery] = useState(initialAddress ? formatAddress(initialAddress) : "");
  const [searchResults, setSearchResults] = useState<google.maps.places.Place[]>([]);
  const [selectedPlace, setSelectedPlace] = useState<google.maps.places.Place | null>(null);
  const [address, setAddress] = useState<Address | null>(null);

  useEffect(() => {
    if (searchResults.length === 1) {
      setSelectedPlace(searchResults[0]);
    } else {
      setSelectedPlace(null);
    }
  }, [searchResults]);

  useEffect(() => {
    if (selectedPlace) {
      selectedPlace
        .fetchFields({
          fields: ["displayName", "location", "addressComponents"],
        })
        .then(({ place }) => {
          const address = getAddressDetails(place);
          setAddress(address);
        });
    }
  }, [onSelectAddress, selectedPlace]);

  return (
    <LargeDialog
      title="Välj adress"
      onClose={onClose}
      sx={{ ".MuiDialogContent-root": { padding: 0 } }}
      actions={
        <>
          <Button variant="outlined" startIcon={<Clear />} onClick={onClose}>
            Avbryt
          </Button>

          <Button
            variant="contained"
            startIcon={<Check />}
            disabled={!address}
            onClick={() => {
              if (address) {
                onSelectAddress(address);
                onClose();
              }
            }}
          >
            Bekräfta
          </Button>
        </>
      }
    >
      <Box sx={{ width: "100%", height: "400px" }}>
        <Map>
          <MapToolbar>
            <MapSearchField value={query} onChange={setQuery} onResults={setSearchResults} />
          </MapToolbar>

          {searchResults.map((place) => (
            <AdvancedMarker
              key={place.id}
              position={place.location}
              onClick={() => setSelectedPlace(place)}
            >
              {place.id === selectedPlace?.id ? null : <Dot size="small" />}
            </AdvancedMarker>
          ))}
        </Map>
      </Box>
    </LargeDialog>
  );
};

export default AddressPickerDialog;
